/* eslint-disable security/detect-object-injection */
import BigNumber from "bignumber.js";
export const assert = (assertion, sentence) => {
    if (!assertion) {
        throw new Error(`Failed assertion${sentence ? `: ${sentence}` : ""}`);
    }
    return true;
};
/**
 * The following is a set of rudimentary type validation functions.
 *
 * The main function is `assertType`, which accepts a type and a dictionary of
 * values.
 *
 * The type must be a string that matches the following pattern:
 *
 * ```
 * TYPE:
 *   | TYPE '|' TYPE
 *   | Array<TYPE>
 *   | TYPE[]
 *   | PRIMITIVE_TYPE
 *
 * PRIMITIVE_TYPE:
 *   | "string"
 *   | "number"
 *   | "bigint"
 *   | "boolean"
 *   | "symbol"
 *   | "undefined"
 *   | "object"
 *   | "function"
 *   | "null"
 *   | "any"
 *   | "Buffer"
 *   | "BigNumber"
 * ```
 *
 * Types are matched by a regex so '|' can't be used at multiple levels, e.g.
 * `string | Array<string | number>`.
 */
export const assertType = (type, objects) => {
    if (isArrayType(type)) {
        return assertArray(type, objects);
    }
    if (isUnionType(type)) {
        return assertTypeUnion(type, objects);
    }
    return assertTypeCheck(is(type), objects, type);
};
const typeOf = (v) => v === null
    ? "null"
    : BigNumber.isBigNumber(v)
        ? "BigNumber"
        : Buffer.isBuffer(v)
            ? "Buffer"
            : typeof v;
const assertTypeCheck = (type, objects, typeDescription) => {
    for (const key of Object.keys(objects)) {
        // eslint-disable-next-line security/detect-object-injection
        const value = objects[key];
        if (!type(value, key)) {
            const readableType = Array.isArray(value) ? "any[]" : typeOf(value);
            throw new Error(`Expected ${key} to be of type '${typeDescription}', instead got '${readableType}'.`);
        }
    }
    return true;
};
const is = (type) => (v) => type === "any" ? true : typeOf(v) === type;
const isUnionType = (unionType) => {
    const types = unionType.split(" | ");
    return types.length > 1 ? types : false;
};
const isArrayType = (arrayType) => {
    // Check with simple string operations to avoid running slow RegExs if there
    // isn't a match.
    const isArray = arrayType.slice(0, 6) === "Array<" && arrayType.slice(-1) === ">";
    if (isArray) {
        const arrayMatch = /^Array<(.*)>$/.exec(arrayType);
        if (arrayMatch) {
            const [, type] = arrayMatch;
            return type;
        }
    }
    const isBracketArray = arrayType.indexOf(" ") === -1 && arrayType.slice(-2) === "[]";
    if (isBracketArray) {
        const bracketMatch = /^([^ ]*)\[\]$/.exec(arrayType);
        if (bracketMatch) {
            const [, type] = bracketMatch;
            return type;
        }
    }
    return false;
};
const assertTypeUnion = (unionType, objects) => {
    const types = unionType.split(" | ");
    return assertTypeCheck((v, key) => types.reduce((acc, type) => {
        if (acc) {
            return acc;
        }
        if (isArrayType(type)) {
            try {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                assertArray(type, { [key]: v });
                return true;
            }
            catch (error) {
                return false;
            }
        }
        return is(type)(v);
    }, false), objects, unionType);
};
const assertArray = (arrayType, objects) => {
    const type = isArrayType(arrayType);
    if (!type) {
        throw new Error(`Invalid array type ${arrayType}`);
    }
    for (const key of Object.keys(objects)) {
        const value = objects[key];
        assertTypeCheck((v) => Array.isArray(v), { value }, "any[]");
        for (let i = 0; i < value.length; i++) {
            assertType(type, { [`${key}[${i}]`]: value[i] });
        }
    }
    return true;
};
export const assertObject = (fieldTypes, objects) => {
    for (const key of Object.keys(objects)) {
        const value = objects[key];
        for (const field of Object.keys(fieldTypes)) {
            if (typeof fieldTypes[field] === "object") {
                assertObject(fieldTypes[field], {
                    [`${key}["${field}"]`]: value[field],
                });
            }
            else if (typeof fieldTypes[field] === "string") {
                assertType(fieldTypes[field], {
                    [`${key}["${field}"]`]: value[field],
                });
            }
            else {
                throw new Error(`Invalid object type definition ${typeof fieldTypes[field]}`);
            }
        }
    }
    return true;
};
//# sourceMappingURL=assert.js.map